import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ShiftNoteAttachments } from "@/components/ShiftNoteAttachments";
import { SmsButton } from "@/components/SendSmsDialog";
import { Users, Search, Loader2, Star, Ban, Save, ShieldAlert, ArrowLeft, Plus, MapPin, Phone, Trash2, UserCog, FileText, Lock, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { LinksSection } from "@/components/ParticipantLinks";
import { CarePlansTab } from "@/components/CarePlansTab";
import { ServiceAgreementsTab } from "@/components/ServiceAgreementsTab";
import { ExternalDocumentsTab } from "@/components/ExternalDocumentsTab";
import { ComplianceCard } from "@/components/ComplianceCard";
import { ParticipantOfficeNotes } from "@/components/ParticipantOfficeNotes";
import { ParticipantCheckIns } from "@/components/ParticipantCheckIns";
import { ParticipantBudget } from "@/components/ParticipantBudget";
import { DocumentAccessLog } from "@/components/DocumentAccessLog";
import { AddressField } from "@/components/AddressField";
import { AddressToShifts } from "@/components/AddressToShifts";
import { MergeParticipantsWizard } from "@/components/MergeParticipantsWizard";
import { PortalAccessCard } from "@/components/PortalAccessCard";
import { RichTextEditor } from "@/components/RichTextEditor";
import { toast } from "sonner";
import { notesInRange, exportStatus } from "@/lib/shiftNotes";
import { ShiftNotesExportDialog } from "@/components/ShiftNotesExportDialog";

const CRIMSON = "var(--brand)";
const CLEAR = "__clear__";

const SEX = ["Not Known", "Male", "Female", "Not Specified"];
const GENDER = ["Male", "Female", "Intersex", "Non-binary", "Unspecified", "Prefer not to say"];
const LIVES = ["No", "Yes"];
const CONTACT_PREF = ["Phone", "Email"];
const YES_NO = ["No", "Yes"];
const AUDIT = ["No - I consent to my records being part of the Audit process", "Yes - I wish to opt-out of the Quality Audit process"];
const STATUS = ["Onboarding", "Active", "Archived"];
const STATE = ["QLD", "NSW", "VIC", "TAS", "SA", "WA", "NT", "ACT"];
const ADDR_LABEL = ["Residential", "Postal", "Work", "Billing", "Other"];

const STATUS_STYLE = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Onboarding: "bg-amber-50 text-amber-700 border-amber-200",
  Archived: "bg-slate-100 text-slate-500 border-slate-200",
};

const GROUPS = [
  { title: "Personal", fields: [
    { k: "salutation", l: "Salutation" }, { k: "first_name", l: "First name", req: true },
    { k: "middle_name", l: "Middle name" }, { k: "last_name", l: "Last name", req: true },
    { k: "preferred_name", l: "Preferred name" }, { k: "pronouns", l: "Pronouns" },
    { k: "sex", l: "Sex", opts: SEX }, { k: "gender", l: "Gender", opts: GENDER },
    { k: "date_of_birth", l: "Date of birth", type: "date", req: true },
    { k: "lives_alone", l: "Lives alone?", opts: LIVES, req: true },
    { k: "religion", l: "Religion" }, { k: "nationality", l: "Nationality" }, { k: "language", l: "Language" },
    { k: "cultural_background_and_ethnicity", l: "Cultural background & ethnicity", type: "textarea", full: true },
    { k: "legal_guardians", l: "Legal guardians", type: "textarea", full: true, ro: true, hint: "Set from Profile → Addresses & Contacts: flag a contact as a guardian." },
    { k: "decision_makers", l: "Decision makers", type: "textarea", full: true, ro: true, hint: "Set from Profile → Addresses & Contacts: flag a contact as a decision maker." },
    { k: "informal_decision_makers", l: "Informal decision makers", type: "textarea", full: true, ro: true, hint: "Set from Profile → Addresses & Contacts: flag a contact as an informal decision maker." },
    { k: "procedures_for_unsuccessful_contact_at_shift_start", l: "Procedure if contact unsuccessful at shift start", type: "textarea", req: true, full: true },
  ] },
  { title: "Contact details", fields: [
    { k: "mobile_number", l: "Mobile number" }, { k: "phone_number", l: "Phone number" }, { k: "email", l: "Email" },
    { k: "contact_preference", l: "Contact preference", opts: CONTACT_PREF },
    { k: "contact_via_guardian_only", l: "Contact via guardian only?", opts: YES_NO, hint: "Yes = office and staff contact the participant's legal guardian, not the participant. Shown on the roster contact card." },
    { k: "additional_contact_preference", l: "Additional contact preference", type: "textarea", full: true },
  ] },
  { title: "Service & NDIS", fields: [
    { k: "holiday_list", l: "Holiday list", req: true, dyn: true, hint: "Regional public holidays (e.g. Ekka, local show days) used for shift rates, billing and agreements." },
    { k: "ndis_number", l: "NDIS number", req: true }, { k: "crn_number", l: "CRN number" },
    { k: "ndis_plan_start_date", l: "Plan start date", type: "date" }, { k: "ndis_plan_end_date", l: "Plan end date", type: "date" },
    { k: "sa_start_date", l: "SA start date", type: "date", ro: true, roLabel: "From agreement", hint: "Set automatically when a Service Agreement is activated (see Documents → Service Agreements)." },
    { k: "sa_end_date", l: "SA end date", type: "date", ro: true, roLabel: "From agreement", hint: "Set automatically when a Service Agreement is activated (see Documents → Service Agreements)." },
    { k: "medicare_number", l: "Medicare number" }, { k: "medicare_position_number", l: "Medicare position #" },
    { k: "medicare_expiry", l: "Medicare expiry", type: "date" }, { k: "companion_card", l: "Companion card" },
    { k: "opt_out_of_quality_audit", l: "Quality audit consent", opts: AUDIT, req: true, full: true },
  ] },
  { title: "General client info", fields: [
    { k: "briefing_on_client", l: "Things to be aware of before first shift", type: "textarea", full: true },
    { k: "communication_requirements", l: "Communication requirements", type: "textarea", full: true },
    { k: "living_arrangements", l: "Living arrangements", type: "textarea", full: true },
    { k: "get_to_know_me", l: "Get to know me", type: "textarea", full: true },
    { k: "hobbies", l: "Hobbies", type: "textarea", full: true },
    { k: "ndis_short_term_goals", l: "NDIS short-term goals", type: "textarea", full: true },
    { k: "ndis_medium_to_long_term_goals", l: "NDIS medium / long-term goals", type: "textarea", full: true },
  ] },
];

const EDIT_KEYS = ["status", "next_review_date", "joined_date", "emergency_instructions", "rostering_considerations", "incompatible_staff_reason", ...GROUPS.flatMap((g) => g.fields.map((f) => f.k))];

function Field({ f, value, onChange }) {
  const id = `pf-${f.k}`;
  return (
    <div className={f.full ? "sm:col-span-2" : ""}>
      <Label htmlFor={id} className="text-[11px] font-semibold text-slate-600">{f.l}{f.req && <span className="text-rose-500"> *</span>}{f.ro && <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-wide text-slate-400"><Lock className="h-2.5 w-2.5" />{f.roLabel || "From contacts"}</span>}</Label>
      {f.opts ? (
        <Select value={value || ""} onValueChange={(v) => onChange(f.k, v === CLEAR ? "" : v)}>
          <SelectTrigger id={id} className="mt-1 h-9 text-sm" data-testid={`pf-${f.k}`}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {!f.req && <SelectItem value={CLEAR} className="text-slate-400">—</SelectItem>}
            {f.opts.map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : f.type === "textarea" ? (
        <Textarea id={id} value={value || ""} disabled={f.ro} onChange={(e) => onChange(f.k, e.target.value)} className="mt-1 text-sm min-h-[64px]" data-testid={`pf-${f.k}`} />
      ) : (
        <Input id={id} type={f.type === "date" ? "date" : "text"} value={value || ""} disabled={f.ro} onChange={(e) => onChange(f.k, e.target.value)} className="mt-1 h-9 text-sm" data-testid={`pf-${f.k}`} />
      )}
      {f.hint && <p className="text-[10px] text-slate-400 mt-0.5">{f.hint}</p>}
    </div>
  );
}

export default function Participants() {
  const { can, user } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("current"); // "current" = everyone not archived
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState("details");
  const [lastTab, setLastTab] = useState({}); // section id -> sub-tab last used in it
  const [docCats, setDocCats] = useState([]);
  const [complianceKey, setComplianceKey] = useState(0);
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState(false);
  // rostering picker
  const [preferred, setPreferred] = useState([]);
  const [incompatible, setIncompatible] = useState([]);
  const [prefQ, setPrefQ] = useState("");
  const [incompatQ, setIncompatQ] = useState("");
  const [savingAccess, setSavingAccess] = useState(false);
  // create
  const [createOpen, setCreateOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newP, setNewP] = useState({});
  const [holidayLists, setHolidayLists] = useState([]);
  useEffect(() => { api.participantHolidayLists().then(setHolidayLists).catch(() => {}); }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const photoInputRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);

  const onPhotoPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !active) return;
    setPhotoBusy(true);
    try {
      const res = await api.uploadParticipantPhoto(active.name, f);
      setDetail((d) => ({ ...d, photo: res.photo }));
      setParticipants((list) => list.map((p) => p.name === active.name ? { ...p, photo: res.photo } : p));
      toast.success("Photo updated.");
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't upload that photo."); }
    finally { setPhotoBusy(false); }
  };
  const removePhoto = async () => {
    if (!active) return;
    setPhotoBusy(true);
    try {
      await api.deleteParticipantPhoto(active.name);
      setDetail((d) => ({ ...d, photo: null }));
      setParticipants((list) => list.map((p) => p.name === active.name ? { ...p, photo: null } : p));
      toast.success("Photo removed.");
    } catch { toast.error("Couldn't remove the photo."); }
    finally { setPhotoBusy(false); }
  };

  const load = () => api.participants().then(setParticipants).catch(() => toast.error("Could not load participants."));

  useEffect(() => {
    Promise.all([api.participants(), api.staff()])
      .then(([p, s]) => { setParticipants(p); setStaff(s); })
      .catch(() => toast.error("Could not load participants."))
      .finally(() => setLoading(false));
    api.docCategories().then(setDocCats).catch(() => {});
    api.complianceScores().then(setScores).catch(() => {});
  }, []);

  const staffName = (id) => staff.find((s) => s.name === id)?.display_name || id;

  const open = async (p) => {
    setActive(p); setDetail(null); setTab("details"); setLastTab({}); setPrefQ(""); setIncompatQ("");
    try {
      const d = await api.participant(p.name);
      setDetail(d);
      setForm(Object.fromEntries(EDIT_KEYS.map((k) => [k, d[k] ?? ""]).concat([
        ["participant_address", (d.participant_address || []).map((r) => ({ ...r }))],
        ["participant_contacts", (d.participant_contacts || []).map((r) => ({ ...r }))],
        ["stakeholders", (d.stakeholders || []).map((r) => ({ ...r }))],
        ["point_of_contact", (d.point_of_contact || []).map((r) => ({ employee: r.employee, employee_name: r.employee_name }))],
        ["second_point_of_contact", (d.second_point_of_contact || []).map((r) => ({ employee: r.employee, employee_name: r.employee_name }))],
      ])));
      setPreferred((d.preferred_staff || []).map((r) => r.staff).filter(Boolean));
      setIncompatible((d.incompatible_staff || []).map((r) => r.staff).filter(Boolean));
    } catch { toast.error("Could not load participant."); }
  };

  // Deep link: /participants?open=<name> auto-opens that participant (e.g. from the Directory).
  useEffect(() => {
    const name = searchParams.get("open");
    if (!name || !participants.length) return;
    if (active && active.name === name) return;
    const p = participants.find((x) => x.name === name);
    if (p) {
      open(p);
      const want = searchParams.get("tab");
      if (want && sections.some((sec) => sec.tabs.some((t) => t.v === want))) pickTab(want);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line
  }, [participants, searchParams]);

  // Two-level navigation: a few sections, each with its own row of pages. The sub-tab
  // ids (`tab`) are what the content below is keyed on.
  const sections = [
    { id: "profile", label: "Profile", tabs: [
      { v: "details", l: "Details" }, { v: "addresses", l: "Addresses & Contacts" }] },
    { id: "documents", label: "Documents", tabs: [
      ...docCats.map((c) => ({ v: c.id, l: c.name, tid: `tab-cat-${c.id}` })),
      { v: "external-docs", l: "External Documents" }, { v: "service-agreements", l: "Service Agreements" }] },
    { id: "compliance", label: "Compliance & Notes", tabs: [
      { v: "compliance", l: "Compliance" }, { v: "office-notes", l: "Office Notes" }] },
    { id: "rostering", label: "Rostering", tabs: [
      { v: "rostering", l: "Rostering" }, { v: "budget", l: "Budget" }, { v: "shift-notes", l: "Shift Notes" }] },
  ];
  const sectionOf = (v) => sections.find((sec) => sec.tabs.some((t) => t.v === v)) || sections[0];
  const pickTab = (v) => { setTab(v); setLastTab((m) => ({ ...m, [sectionOf(v).id]: v })); };
  const pickSection = (sec) => {
    const keep = lastTab[sec.id];
    pickTab(sec.tabs.some((t) => t.v === keep) ? keep : sec.tabs[0].v);
  };
  const curSection = sectionOf(tab);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    for (const g of GROUPS) for (const f of g.fields)
      if (f.req && !String(form[f.k] || "").trim()) { toast.error(`${f.l} is required.`); return; }
    setSaving(true);
    try {
      await api.updateParticipant(active.name, form);
      toast.success("Participant saved.");
      load();
      const d = await api.participant(active.name);
      setDetail(d);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  // addresses
  const addAddress = () => setForm((f) => ({ ...f, participant_address: [...(f.participant_address || []), { address_label: "Residential", is_primary: (f.participant_address || []).length === 0, address_search: "", state: "QLD" }] }));
  const setAddr = (i, k, v) => setForm((f) => { const a = [...(f.participant_address || [])]; a[i] = { ...a[i], [k]: v }; if (k === "is_primary" && v) a.forEach((r, j) => { if (j !== i) r.is_primary = false; }); return { ...f, participant_address: a }; });
  const patchAddr = (i, patch) => setForm((f) => { const a = [...(f.participant_address || [])]; a[i] = { ...a[i], ...patch }; return { ...f, participant_address: a }; });
  const removeAddr = (i) => setForm((f) => ({ ...f, participant_address: (f.participant_address || []).filter((_, j) => j !== i) }));

  // rostering
  const toggle = (list, setList, otherList, setOther, id) => {
    if (list.includes(id)) setList(list.filter((x) => x !== id));
    else { setList([...list, id]); if (otherList.includes(id)) setOther(otherList.filter((x) => x !== id)); }
  };
  const orderedStaff = (selectedList, query) => {
    const t = query.trim().toLowerCase();
    return {
      selected: staff.filter((s) => selectedList.includes(s.name)),
      rest: staff.filter((s) => !selectedList.includes(s.name) && s.status === "Active" && (s.display_name || s.name).toLowerCase().includes(t)),
    };
  };
  const saveAccess = async () => {
    setSavingAccess(true);
    try {
      const r = await api.setParticipantStaffAccess(active.name, { preferred, incompatible });
      if (r.auto_unassigned_count > 0) toast.warning(`Saved. ${r.auto_unassigned_count} upcoming shift(s) auto-unassigned and flagged.`);
      else toast.success("Access lists saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingAccess(false); }
  };

  const createParticipant = async () => {
    const req = ["first_name", "last_name", "date_of_birth", "ndis_number", "holiday_list", "lives_alone", "opt_out_of_quality_audit", "procedures_for_unsuccessful_contact_at_shift_start"];
    for (const k of req) if (!String(newP[k] || "").trim()) { toast.error("Please fill all required fields."); return; }
    setCreating(true);
    try {
      const r = await api.createParticipant({ status: "Onboarding", ...newP });
      toast.success("Participant created.");
      setCreateOpen(false); setNewP({});
      await load();
      open({ name: r.name, display_name: `${newP.first_name} ${newP.last_name}` });
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed."); }
    finally { setCreating(false); }
  };

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return participants.filter((p) =>
      (statusFilter === "all" || (statusFilter === "current" ? (p.status || "Active") !== "Archived" : (p.status || "Active") === statusFilter)) &&
      ((p.display_name || p.name).toLowerCase().includes(t) || (p.ndis_number || "").toLowerCase().includes(t)));
  }, [participants, q, statusFilter]);

  if (loading) return <div className="flex justify-center pt-24"><Loader2 className="h-6 w-6 animate-spin" style={{ color: CRIMSON }} /></div>;

  // ---------------- Detail view ----------------
  if (active) {
    return (
      <div className="p-6 max-w-4xl mx-auto" data-testid="participant-detail">
        <button onClick={() => setActive(null)} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-3" data-testid="participant-back">
          <ArrowLeft className="h-3.5 w-3.5" /> All participants
        </button>
        {!detail ? (
          <div className="flex justify-center pt-16"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-4">
                {detail.photo
                  ? <img src={detail.photo} alt={detail.display_name || detail.name} className="h-16 w-16 rounded-full object-cover border border-slate-200 shrink-0" data-testid="participant-photo" />
                  : <div className="h-16 w-16 rounded-full flex items-center justify-center text-lg font-semibold text-white shrink-0" style={{ backgroundColor: CRIMSON }} data-testid="participant-photo-fallback">{(detail.display_name || detail.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</div>}
                <div>
                  <h1 className="text-xl font-semibold font-brand text-slate-800">{detail.display_name || detail.name}</h1>
                  <div className="text-xs text-slate-400 mt-0.5">{detail.name}{detail.ndis_number ? ` · NDIS ${detail.ndis_number}` : ""}{detail.participant_age ? ` · ${detail.participant_age}` : ""}</div>
                  {can.has("participants.edit") && (
                    <div className="flex items-center gap-3 mt-1.5">
                      <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoPick} className="hidden" data-testid="participant-photo-input" />
                      <button onClick={() => photoInputRef.current?.click()} disabled={photoBusy} className="text-xs inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 disabled:opacity-50" data-testid="participant-photo-upload">
                        {photoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCog className="h-3.5 w-3.5" />}{detail.photo ? "Change photo" : "Add photo"}
                      </button>
                      {detail.photo && <button onClick={removePhoto} disabled={photoBusy} className="text-xs inline-flex items-center gap-1 text-rose-600 hover:text-rose-800 disabled:opacity-50" data-testid="participant-photo-remove"><Trash2 className="h-3.5 w-3.5" />Remove</button>}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {can.has("sms.send") && detail.mobile_number && (
                  <SmsButton type="participant" id={detail.name} name={detail.display_name || detail.name} mobile={detail.mobile_number} guardianOnly={detail.contact_via_guardian_only === "Yes"} />
                )}
                {can.has("participants.edit") && (tab === "details" || tab === "addresses") && (
                  <Select value={form.status || "Active"} onValueChange={(v) => setField("status", v)}>
                    <SelectTrigger className="h-9 w-36 text-xs" data-testid="participant-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {can.has("participants.edit") && <Button onClick={save} disabled={saving} style={{ backgroundColor: CRIMSON }} data-testid="participant-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save
                </Button>}
              </div>
            </div>

            <Tabs value={tab} onValueChange={pickTab}>
              <div role="tablist" aria-label="Participant sections" className="flex gap-1 border-b border-slate-200 mb-3" data-testid="participant-sections">
                {sections.map((sec) => {
                  const on = sec.id === curSection.id;
                  return (
                    <button key={sec.id} type="button" role="tab" aria-selected={on} onClick={() => pickSection(sec)}
                      className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${on ? "border-brand text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                      data-testid={`section-${sec.id}`}>{sec.label}</button>
                  );
                })}
              </div>
              <TabsList className="h-auto flex-wrap justify-start" data-testid="participant-tabs">
                {curSection.tabs.map((t) => <TabsTrigger key={t.v} value={t.v} data-testid={t.tid || `tab-${t.v}`}>{t.l}</TabsTrigger>)}
              </TabsList>

              {/* DETAILS */}
              <TabsContent value="details" className="space-y-5 mt-4">
                <div className="grid sm:grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-white p-4">
                  <Field f={{ k: "joined_date", l: "Joined date", type: "date" }} value={form.joined_date} onChange={setField} />
                  <Field f={{ k: "next_review_date", l: "Next review date", type: "date" }} value={form.next_review_date} onChange={setField} />
                </div>
                {GROUPS.map((g) => (
                  <div key={g.title} className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">{g.title}</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {g.fields.map((f) => <Field key={f.k} f={f.dyn ? { ...f, opts: holidayLists } : f} value={form[f.k]} onChange={setField} />)}
                    </div>
                  </div>
                ))}
              </TabsContent>

              {/* ADDRESSES & CONTACTS */}
              <TabsContent value="addresses" className="space-y-5 mt-4">
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Addresses</div>
                    {can.has("participants.edit") && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addAddress} data-testid="add-address"><Plus className="h-3.5 w-3.5 mr-1" />Add address</Button>}
                  </div>
                  <div className="space-y-3">
                    {(form.participant_address || []).length === 0 && <div className="text-xs text-slate-400">No addresses yet.</div>}
                    {(form.participant_address || []).map((a, i) => (
                      <div key={i} className="rounded-md border border-slate-200 p-3 space-y-2" data-testid={`address-row-${i}`}>
                        <div className="flex items-center gap-2">
                          <Select value={a.address_label || "Residential"} onValueChange={(v) => setAddr(i, "address_label", v)}>
                            <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{ADDR_LABEL.map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}</SelectContent>
                          </Select>
                          <label className="flex items-center gap-1 text-[11px] text-slate-600"><input type="checkbox" checked={!!a.is_primary} onChange={(e) => setAddr(i, "is_primary", e.target.checked)} className="accent-brand" data-testid={`address-primary-${i}`} /> Primary</label>
                          <div className="flex-1" />
                          {can.has("participants.edit") && <button onClick={() => removeAddr(i)} className="text-slate-400 hover:text-rose-600" data-testid={`address-remove-${i}`}><Trash2 className="h-4 w-4" /></button>}
                        </div>
                        <AddressField value={a.address_search || ""} disabled={!can.has("participants.edit")}
                          onChange={(v) => setAddr(i, "address_search", v)}
                          parts={a} onPartsChange={(next) => patchAddr(i, next)}
                          testid={`address-search-${i}`} />
                        <AddressToShifts participant={active?.name} index={i}
                          original={((detail?.participant_address || [])[i] || {}).address_search || ""}
                          current={a.address_search || ""} canEdit={can.has("participants.edit")} />
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2">Address changes save with the participant (use the <strong>Save</strong> button in the top-right). Past shifts keep the address the support was delivered at.</p>
                  {(detail?.address_history || []).length > 0 && (
                    <details className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="address-history">
                      <summary className="text-[11px] font-semibold text-slate-600 cursor-pointer">
                        Past address history ({detail.address_history.length})
                      </summary>
                      <div className="mt-2 space-y-1.5">
                        {[...detail.address_history].reverse().map((h, i) => (
                          <div key={i} className="text-[11px] text-slate-600" data-testid={`address-history-${i}`}>
                            <span className="font-medium text-slate-700">{h.address}</span>
                            <span className="text-slate-400"> · {h.label}{h.was_primary ? " (primary)" : ""} · changed {h.changed_on} by {h.changed_by}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>

                {/* Contacts */}
                <LinksSection kindKey="contact" canEdit={can.has("participants.edit")} participant={active?.name}
                  rows={form.participant_contacts || []}
                  setRows={(rows) => setField("participant_contacts", rows)} />

                {/* Stakeholders */}
                <LinksSection kindKey="stakeholder" canEdit={can.has("participants.edit")} participant={active?.name}
                  rows={form.stakeholders || []}
                  setRows={(rows) => setField("stakeholders", rows)} />

                <PortalAccessCard participant={active.name} />

                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <Field f={{ k: "emergency_instructions", l: "Emergency escalation & special instructions", type: "textarea", full: true }} value={form.emergency_instructions} onChange={setField} />
                </div>
              </TabsContent>

              {/* CARE PLANS — one tab per document category */}
              {docCats.map((c) => (
                <TabsContent key={c.id} value={c.id}>
                  <CarePlansTab participant={active.name} categoryId={c.id} onChanged={() => setComplianceKey((k) => k + 1)} />
                </TabsContent>
              ))}

              {/* EXTERNAL DOCUMENTS */}
              <TabsContent value="external-docs">
                <ExternalDocumentsTab participant={active.name} onChanged={() => setComplianceKey((k) => k + 1)} />
              </TabsContent>

              {/* COMPLIANCE */}
              <TabsContent value="compliance" className="space-y-4">
                <ComplianceCard participant={active.name} refreshKey={complianceKey} />
                <ParticipantCheckIns participant={active.name} canEdit={can.has("participants.notes")} />
                <DocumentAccessLog participant={active.name} refreshKey={complianceKey} />
              </TabsContent>

              {/* OFFICE NOTES — office staff only */}
              <TabsContent value="office-notes">
                <ParticipantOfficeNotes participant={active.name} canEdit={can.has("participants.notes")} user={user} isAdmin={can.has("participants.notes_delete_any")} />
              </TabsContent>

              {/* ROSTERING */}
              <TabsContent value="rostering" className="space-y-5 mt-4">
                <div className="grid sm:grid-cols-2 gap-5 rounded-lg border border-slate-200 bg-white p-4">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><UserCog className="h-3.5 w-3.5 text-slate-500" />Point of contact (Team Leader)</div>
                    <StaffTablePicker value={form.point_of_contact || []} staff={staff} disabled={!can.has("participants.edit")} testid="poc" onChange={(rows) => setField("point_of_contact", rows)} />
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><UserCog className="h-3.5 w-3.5 text-slate-500" />Secondary point of contact (Lead Worker)</div>
                    <StaffTablePicker value={form.second_point_of_contact || []} staff={staff} disabled={!can.has("participants.edit")} testid="poc2" onChange={(rows) => setField("second_point_of_contact", rows)} />
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-slate-500" />Rostering considerations</div>
                  <RichTextEditor value={form.rostering_considerations || ""} disabled={!can.has("participants.edit")} onChange={(v) => setField("rostering_considerations", v)} testid="rostering-considerations" placeholder="Notes the roster team should keep in mind (availability, routines, ratios…)" />
                </div>

                <div className="flex items-start gap-2 rounded-lg bg-rose-50 border border-rose-100 p-2.5 text-[11px] text-rose-700">
                  <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>Adding a worker to <b>Incompatible</b> immediately removes them from that participant's upcoming shifts and flags those shifts on the roster.</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-5 rounded-lg border border-slate-200 bg-white p-4">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-amber-500" />Preferred workers</div>
                    {renderPicker(staff, orderedStaff, toggle, preferred, setPreferred, incompatible, setIncompatible, prefQ, setPrefQ, "accent-amber-500", "pref")}
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Ban className="h-3.5 w-3.5 text-rose-500" />Incompatible workers</div>
                    {renderPicker(staff, orderedStaff, toggle, incompatible, setIncompatible, preferred, setPreferred, incompatQ, setIncompatQ, "accent-brand", "incompat")}
                  </div>
                </div>
                {can.has("participants.edit") && <Button onClick={saveAccess} disabled={savingAccess} style={{ backgroundColor: CRIMSON }} data-testid="participant-access-save">
                  {savingAccess ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save access lists
                </Button>}

                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Ban className="h-3.5 w-3.5 text-rose-500" />Incompatible staff reason</div>
                  <RichTextEditor value={form.incompatible_staff_reason || ""} disabled={!can.has("participants.edit")} onChange={(v) => setField("incompatible_staff_reason", v)} testid="incompatible-staff-reason" placeholder="Why certain workers are marked incompatible…" />
                  <p className="text-[10px] text-slate-400 mt-2">Points of contact and these notes save with the participant (use the <strong>Save</strong> button in the top-right). Preferred/Incompatible worker lists save with the <strong>Save access lists</strong> button above.</p>
                </div>
              </TabsContent>

              {/* SERVICE AGREEMENTS */}
              <TabsContent value="service-agreements" className="mt-4">
                <ServiceAgreementsTab participant={active.name} participantName={detail.display_name || detail.name} canEdit={can.has("sa.edit")} canDelete={can.has("participant_docs.delete_signed")} />
              </TabsContent>

              {/* BUDGET (read-only) */}
              <TabsContent value="budget" className="mt-4">
                {tab === "budget" && <ParticipantBudget participant={active.name} canEdit={can.has("sa.fees")} />}
              </TabsContent>

              {/* SHIFT NOTES (read-only) */}
              <TabsContent value="shift-notes" className="mt-4">
                <ParticipantShiftNotes participant={active.name} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    );
  }

  // ---------------- List view ----------------
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="participants-page">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2.5">
          <Users className="h-5 w-5" style={{ color: CRIMSON }} />
          <h1 className="text-xl font-semibold font-brand text-slate-800">Participants</h1>
        </div>
        <div className="flex items-center gap-2">
        {can.has("participants.merge") && <Button variant="outline" onClick={() => setMergeOpen(true)} data-testid="merge-participants-btn">Merge duplicates</Button>}
        {can.has("participants.edit") && <Button onClick={() => { setNewP({}); setCreateOpen(true); }} style={{ backgroundColor: CRIMSON }} data-testid="new-participant-btn"><Plus className="h-4 w-4 mr-1.5" />New participant</Button>}
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">View and manage participant profiles, addresses, contacts and rostering preferences.</p>

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or NDIS #…" className="h-9 pl-9 text-sm" data-testid="participants-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="participants-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="current" className="text-sm">Active &amp; onboarding</SelectItem>
            <SelectItem value="all" className="text-sm">All statuses</SelectItem>
            {STATUS.map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="participants-list">
        {filtered.map((p) => (
          <button key={p.name} onClick={() => open(p)} data-testid={`participant-card-${p.name}`}
            className="text-left rounded-lg border border-slate-200 bg-white p-3.5 hover:border-brand hover:shadow-sm transition-all">
            <div className="flex items-start gap-2.5">
              {p.photo
                ? <img src={p.photo} alt={p.display_name || p.name} className="h-10 w-10 rounded-full object-cover shrink-0" data-testid={`participant-card-photo-${p.name}`} />
                : <div className="h-10 w-10 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shrink-0" style={{ backgroundColor: CRIMSON }}>{(p.display_name || p.name).split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-sm text-slate-800 truncate">{p.display_name || p.name}</div>
                  <Badge className={`text-[9px] py-0 shrink-0 ${STATUS_STYLE[p.status] || STATUS_STYLE.Active}`}>{p.status || "Active"}</Badge>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">{p.ndis_number ? `NDIS ${p.ndis_number}` : p.name}</div>
                {p.mobile_number && <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" />{p.mobile_number}</div>}
                {typeof scores[p.name] === "number" && (
                  <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5"
                    style={{ color: scores[p.name] >= 85 ? "#16a34a" : scores[p.name] >= 60 ? "#b45309" : "#dc2626",
                             backgroundColor: scores[p.name] >= 85 ? "#dcfce7" : scores[p.name] >= 60 ? "#fef3c7" : "#fee2e2" }}
                    data-testid={`participant-score-${p.name}`}>
                    Compliance {scores[p.name]}%
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
        {filtered.length === 0 && <div className="text-sm text-slate-400 col-span-full py-8 text-center">No participants match.</div>}
      </div>

      <MergeParticipantsWizard open={mergeOpen} onOpenChange={setMergeOpen} participants={participants} onDone={load} />

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-participant-dialog">
          <DialogHeader><DialogTitle className="font-brand text-base">New participant</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {[{ k: "first_name", l: "First name" }, { k: "last_name", l: "Last name" }, { k: "date_of_birth", l: "Date of birth", type: "date" }, { k: "ndis_number", l: "NDIS number" }].map((f) => (
              <div key={f.k}>
                <Label className="text-[11px] font-semibold text-slate-600">{f.l} <span className="text-rose-500">*</span></Label>
                <Input type={f.type === "date" ? "date" : "text"} value={newP[f.k] || ""} onChange={(e) => setNewP((n) => ({ ...n, [f.k]: e.target.value }))} className="mt-1 h-9 text-sm" data-testid={`np-${f.k}`} />
              </div>
            ))}
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Holiday list <span className="text-rose-500">*</span></Label>
              <Select value={newP.holiday_list || ""} onValueChange={(v) => setNewP((n) => ({ ...n, holiday_list: v }))}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="np-holiday_list"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{holidayLists.map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Lives alone? <span className="text-rose-500">*</span></Label>
              <Select value={newP.lives_alone || ""} onValueChange={(v) => setNewP((n) => ({ ...n, lives_alone: v }))}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="np-lives_alone"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{LIVES.map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Quality audit <span className="text-rose-500">*</span></Label>
              <Select value={newP.opt_out_of_quality_audit || ""} onValueChange={(v) => setNewP((n) => ({ ...n, opt_out_of_quality_audit: v }))}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="np-audit"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{AUDIT.map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px] font-semibold text-slate-600">Procedure if contact unsuccessful at shift start <span className="text-rose-500">*</span></Label>
              <Textarea value={newP.procedures_for_unsuccessful_contact_at_shift_start || ""} onChange={(e) => setNewP((n) => ({ ...n, procedures_for_unsuccessful_contact_at_shift_start: e.target.value }))} className="mt-1 text-sm min-h-[60px]" data-testid="np-procedure" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createParticipant} disabled={creating} style={{ backgroundColor: CRIMSON }} data-testid="create-participant-submit">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function renderPicker(staff, orderedStaff, toggle, selectedList, setList, otherList, setOther, query, setQuery, accent, prefix) {
  const { selected, rest } = orderedStaff(selectedList, query);
  const row = (s) => (
    <label key={s.name} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer py-0.5" data-testid={`${prefix}-${s.name}`}>
      <input type="checkbox" className={accent} checked={selectedList.includes(s.name)} onChange={() => toggle(selectedList, setList, otherList, setOther, s.name)} />
      {s.display_name || s.name}
    </label>
  );
  return (
    <div>
      <div className="relative mb-2">
        <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search staff…" className="h-8 pl-8 text-xs" data-testid={`${prefix}-search`} />
      </div>
      <div className="space-y-1 max-h-52 overflow-y-auto pr-1" data-testid={`${prefix}-list`}>
        {selected.length > 0 && (
          <>
            <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 pt-0.5">Selected ({selected.length})</div>
            {selected.map(row)}
            {rest.length > 0 && <div className="border-t border-slate-100 my-1" />}
          </>
        )}
        {rest.map(row)}
        {selected.length === 0 && rest.length === 0 && <div className="text-[11px] text-slate-400 py-2">No staff match "{query}".</div>}
      </div>
    </div>
  );
}

// Multi-select of staff (NDIS Staff Profiles) stored as {employee, employee_name} child rows.
function StaffTablePicker({ value, staff, disabled, onChange, testid }) {
  const [q, setQ] = useState("");
  const selectedIds = (value || []).map((r) => r.employee);
  const toggle = (s) => {
    if (disabled) return;
    if (selectedIds.includes(s.name)) onChange((value || []).filter((r) => r.employee !== s.name));
    else onChange([...(value || []), { employee: s.name, employee_name: s.display_name || s.name }]);
  };
  const t = q.trim().toLowerCase();
  const selected = staff.filter((s) => selectedIds.includes(s.name));
  const rest = staff.filter((s) => !selectedIds.includes(s.name) && (s.display_name || s.name).toLowerCase().includes(t));
  const row = (s) => (
    <label key={s.name} className={`flex items-center gap-2 text-xs text-slate-700 py-0.5 ${disabled ? "opacity-60" : "cursor-pointer"}`} data-testid={`${testid}-${s.name}`}>
      <input type="checkbox" className="accent-brand" disabled={disabled} checked={selectedIds.includes(s.name)} onChange={() => toggle(s)} />
      {s.display_name || s.name}
    </label>
  );
  return (
    <div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2" data-testid={`${testid}-chips`}>
          {selected.map((s) => (
            <Badge key={s.name} className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">
              {s.display_name || s.name}
              {!disabled && <button onClick={() => toggle(s)} className="ml-1 text-slate-400 hover:text-rose-600" data-testid={`${testid}-remove-${s.name}`}>×</button>}
            </Badge>
          ))}
        </div>
      )}
      {!disabled && (
        <>
          <div className="relative mb-2">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff…" className="h-8 pl-8 text-xs" data-testid={`${testid}-search`} />
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto pr-1" data-testid={`${testid}-list`}>
            {rest.map(row)}
            {rest.length === 0 && <div className="text-[11px] text-slate-400 py-2">No more staff match.</div>}
          </div>
        </>
      )}
      {disabled && selected.length === 0 && <div className="text-[11px] text-slate-400">None set.</div>}
    </div>
  );
}


function ParticipantShiftNotes({ participant }) {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [exporting, setExporting] = useState(false);
  useEffect(() => { api.participantShiftNotes(participant).then(setRows).catch(() => setRows([])); }, [participant]);
  const fmt = (d) => { if (!d) return "—"; const p = String(d).slice(0, 10).split("-"); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
  if (rows === null) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (rows.length === 0) return <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">No shift notes recorded for this participant.</div>;
  const shown = notesInRange(rows, range.from, range.to);
  const Field = ({ label, value, cls }) => value ? (
    <div className="mt-1.5"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}: </span><span className={`text-sm whitespace-pre-wrap ${cls || "text-slate-600"}`}>{value}</span></div>
  ) : null;
  return (
    <div className="space-y-2" data-testid="participant-shift-notes-list">
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
        <div><Label className="text-[11px] text-slate-500">From</Label>
          <Input type="date" value={range.from} max={range.to || undefined} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="h-8 text-xs w-36" data-testid="shift-notes-from" /></div>
        <div><Label className="text-[11px] text-slate-500">To</Label>
          <Input type="date" value={range.to} min={range.from || undefined} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="h-8 text-xs w-36" data-testid="shift-notes-to" /></div>
        {(range.from || range.to) && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setRange({ from: "", to: "" })} data-testid="shift-notes-clear-range">Clear</Button>}
        <span className="text-[11px] text-slate-500 pb-2" data-testid="shift-notes-count">{shown.length} of {rows.length} note{rows.length === 1 ? "" : "s"}</span>
        {can.has("timesheets.view") && (
          <Button size="sm" variant="outline" className="h-8 text-xs ml-auto" onClick={() => setExporting(true)}
            data-testid="shift-notes-download">
            <Download className="h-3.5 w-3.5 mr-1" />Download PDF
          </Button>
        )}
      </div>
      <ShiftNotesExportDialog open={exporting} onOpenChange={setExporting} participant={participant} rows={rows} range={range} />
      {shown.length === 0 && <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">No shift notes in this date range.</div>}
      {shown.map((r) => (
        <div key={r.name} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`participant-shift-note-${r.name}`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className="text-sm font-medium text-slate-800">{r.support_worker_name || "—"}</span>
            <div className="flex flex-col items-end gap-0.5 shrink-0">
              <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[9px] py-0">{exportStatus(r)}</Badge>
              <span className="text-[11px] text-slate-700 font-medium" data-testid={`participant-shift-note-shift-date-${r.name}`}>
                <span className="text-slate-400 font-normal">Shift Date: </span>{fmt(r.shift_date)}
              </span>
              {r.submitted_at && (
                <span className="text-[10px] text-slate-400" data-testid={`participant-shift-note-submitted-${r.name}`}>
                  Submitted: {fmt(r.submitted_at)}
                </span>
              )}
            </div>
          </div>
          <Field label="Events" value={r.events} />
          <Field label="Mood" value={r.mood} />
          <Field label="Important" value={r.urgent_notes} cls="text-amber-700" />
          {r.contacted_office ? <Field label="Contacted office" value={r.office_contact_reason || "Yes"} cls="text-brand" /> : null}
          {(r.attachments || []).length > 0 && (
            <div className="mt-2"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Attachments</span>
              <div className="mt-1"><ShiftNoteAttachments items={r.attachments} load={(idx, preview) => api.shiftNoteAttachment(r.name, idx, preview)} /></div></div>
          )}
        </div>
      ))}
    </div>
  );
}
